/**
 * @file Base drag source component.
 * @author Heino Lenting
 */
import * as tslib_1 from "tslib";
import { OnInit } from '@angular/core';
import { ControlBase } from '../../base/helpers/control-base';
import { ColorTools } from '../../base/helpers/color-tools';
import { DataSet } from '../../core/helpers/data-set';
import { DomainService } from '../../domain.service';
import { SortDescriptor } from '../../core/helpers/sort-descriptor';
import { SortDirection } from '../../core/helpers/sort-direction';
import { GridDragBaseComponent } from '../grid-drag-base/grid-drag-base.component';
import { FormControl } from '@angular/forms';
var DragSourceBaseComponent = /** @class */ (function (_super) {
    tslib_1.__extends(DragSourceBaseComponent, _super);
    /**
     * Creates an instance of DragSourceBaseComponent.
     * @param {configurationsService} configurationsService
     */
    function DragSourceBaseComponent(domainService, executorsService, alertsService, gridService) {
        var _this = _super.call(this, alertsService) || this;
        _this.domainService = domainService;
        _this.executorsService = executorsService;
        _this.alertsService = alertsService;
        _this.gridService = gridService;
        _this.dragSourceBaseControl = new FormControl();
        /**
         * Gets, sets the query options to retrieve the agenda items.
         * @private
         * @type {QueryOptions}
         */
        _this.queryOptions = {};
        _this.dynamicParameters = [];
        // Keep track of the loading state of the drag source
        _this.isLoading = false;
        /** Set of color tools */
        _this.colorTools = new ColorTools();
        /**
         * Keep track of the setting 'timed'.
         * @private
         */
        _this._timed = true;
        _this._objects = undefined;
        _this._filter = null;
        _this.subscription = null;
        _this._objects = new DataSet(domainService);
        // Set default sorting for the drag sources variants
        _this._objects.sorting.push(new SortDescriptor('Name', 'Name', SortDirection.Ascending, 0));
        return _this;
    }
    Object.defineProperty(DragSourceBaseComponent.prototype, "startDate", {
        get: function () {
            return this.gridService.startDate.toDate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DragSourceBaseComponent.prototype, "endDate", {
        get: function () {
            return this.gridService.endDate.toDate();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DragSourceBaseComponent.prototype, "count", {
        get: function () {
            return this.items.length;
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DragSourceBaseComponent.prototype, "teamOid", {
        get: function () {
            return '?';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DragSourceBaseComponent.prototype, "filterOid", {
        get: function () {
            if (this._filter == null) {
                return '?';
            }
            return this._filter['Oid'] ? this._filter['Oid'] : '?';
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DragSourceBaseComponent.prototype, "filter", {
        get: function () {
            return this._filter;
        },
        set: function (value) {
            if (value === this._filter) {
                return;
            }
            this._filter = value;
            this.refresh();
        },
        enumerable: true,
        configurable: true
    });
    Object.defineProperty(DragSourceBaseComponent.prototype, "items", {
        // Temp variable for displaying items
        get: function () {
            if (!this._objects || !this._objects.items) {
                return [];
            }
            return this._objects.items.filter(this.itemFilter);
        },
        enumerable: true,
        configurable: true
    });
    // Add a SortDescriptor to the displaying items
    DragSourceBaseComponent.prototype.addSorting = function (value) {
        this._objects.sorting.push(value);
    };
    Object.defineProperty(DragSourceBaseComponent.prototype, "timed", {
        /**
         * Gets the times property.
         * @type {boolean}
         */
        get: function () {
            return this._timed;
        },
        /**
         * Sets the times property.
         */
        set: function (value) {
            if (this._timed === value) {
                return;
            }
            this._timed = value;
            this.refresh();
        },
        enumerable: true,
        configurable: true
    });
    /**
     * Handling the drag start event
     * @param {DragEvent} event
     * @param {*} item
     */
    DragSourceBaseComponent.prototype.dragItem = function (event, item) {
        // Only proceed if there is an executor selected
        if (!this.selectedExecutorName) {
            this.alertsService.createAlert('danger', 'APP.EXECUTORS.NO_EXECUTOR_SELECTED');
            return;
        }
        // Create executor event and get the id returned
        var executorId = this.executorsService.createExecutorEventAndGetTheId(this.selectedExecutorName, {
            items: [item]
        });
        // Set the executor id
        /* Fix issue: Xerius: Bij aanlog via IE is het niet mogelijk om Afwezigheden /Services te droppen op Planbord #2718
         * Oplossing gebruik alleen 'Text' bij dataTransfer.
         * Zie: https://stackoverflow.com/questions/26213011/html5-dragdrop-issue-in-internet-explorer-datatransfer-property-access-not-pos
         */
        event.dataTransfer.setData('Text', executorId);
    };
    /**
     * No warning when the configuration is missing for drag-source components.
     * This overrides ngonInit in abstract-component.ts. Control-base.ts has no ngonInit.
     */
    DragSourceBaseComponent.prototype.ngOnInit = function () { };
    /**
     * Is called after the block name is set and the configuration is loaded.
     * @protected
     */
    DragSourceBaseComponent.prototype.afterInitialize = function () {
        var _this = this;
        _super.prototype.afterInitialize.call(this);
        if (this.configuration.data) {
            this.isLoading = true;
            var entityConfig = this.configuration.data;
            this._objects.sourceName = entityConfig.dataSource;
            if (entityConfig.parameters) {
                this.queryOptions.parameters = Object.assign({}, entityConfig.parameters);
            }
            this.queryOptions.expand = entityConfig.expand;
            this._objects.queryOptions = this.queryOptions;
            DomainService.getDynamicParams(this.queryOptions, this.dynamicParameters);
            this.domainService.registerSource(entityConfig.entity, this._objects);
            this.refresh();
            this.subscriptionPool.add(this.gridService.stateChanged.subscribe(function (result) {
                _this.refresh();
            }));
        }
    };
    /**
     * Is called before the destruction of the component
     */
    DragSourceBaseComponent.prototype.ngOnDestroy = function () {
        _super.prototype.ngOnDestroy.call(this);
    };
    /**
     * Can be overridden to filter out items
     * @protected
     * @param {Entity} item The item is tested
     * @returns A value indicating whether the item should be included
     */
    DragSourceBaseComponent.prototype.itemFilter = function (item) {
        return true;
    };
    DragSourceBaseComponent.prototype.refresh = function () {
        return tslib_1.__awaiter(this, void 0, void 0, function () {
            return tslib_1.__generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        if (this._objects.entityType == null) {
                            return [2 /*return*/];
                        }
                        this.isLoading = true;
                        DomainService.setDynamicParams(this.queryOptions, this.dynamicParameters, this);
                        return [4 /*yield*/, this._objects.refresh()];
                    case 1:
                        _a.sent();
                        this.isLoading = false;
                        return [2 /*return*/];
                }
            });
        });
    };
    return DragSourceBaseComponent;
}(ControlBase));
export { DragSourceBaseComponent };
